<script setup lang="ts">
  import { computed, ref } from 'vue';

  import BvBtn from '@bv/components/form/button/BvBtn.vue';
  import { trans } from '@bv/utils/i18n';

  import BvAlertProgress from './BvAlertProgress.vue';
  import BvAlertCloseIcon from './icons/BvAlertCloseIcon.vue';
  import BvAlertErrorIcon from './icons/BvAlertErrorIcon.vue';
  import BvAlertInfoIcon from './icons/BvAlertInfoIcon.vue';
  import BvAlertSuccessIcon from './icons/BvAlertSuccessIcon.vue';
  import BvAlertWarningIcon from './icons/BvAlertWarningIcon.vue';

  import type { Component } from 'vue';

  type AlertVariant = 'info' | 'success' | 'warning' | 'error';

  const iconByVariant: Record<AlertVariant, Component> = {
    info: BvAlertInfoIcon,
    success: BvAlertSuccessIcon,
    warning: BvAlertWarningIcon,
    error: BvAlertErrorIcon,
  };

  const props = withDefaults(
    defineProps<{
      variant: AlertVariant;
      title: string;
      message: string;
      action?: {
        text: string;
        onClick: () => void;
        closeOnClick?: boolean;
      };
      closeToast?: () => void;
      autoClose?: number | boolean;
      hideCloseBtn?: boolean;
      freeWidth?: boolean;
      lines?: undefined | 'multiple' | 'single';
      pauseOnHover?: boolean;
    }>(),
    {
      action: undefined,
      closeToast: undefined,
      autoClose: false,
      hideCloseBtn: false,
      freeWidth: false,
      lines: undefined,
      pauseOnHover: true,
    },
  );

  function onActionClick() {
    props.action?.onClick();
    if (props.action?.closeOnClick) {
      props.closeToast?.();
    }
  }

  const Icon = computed(() => iconByVariant[props.variant]);

  const durationMs = computed(() => (typeof props.autoClose === 'number' ? props.autoClose : 5000));

  const isHoverPaused = ref(false);
  const canPauseOnHover = computed(() => Boolean(props.autoClose) && props.pauseOnHover);

  function onMouseEnter() {
    if (canPauseOnHover.value) {
      isHoverPaused.value = true;
    }
  }

  function onMouseLeave() {
    isHoverPaused.value = false;
  }
</script>

<template>
  <div
    class="relative ms-auto box-border flex items-start justify-between gap-4 overflow-hidden rounded-lg border border-solid p-3"
    :class="[
      variant === 'info' && 'border-blue-500 bg-blue-100 text-blue-500',
      variant === 'success' && 'border-green-600 bg-green-100 text-green-600',
      variant === 'warning' && 'border-orange-600 bg-orange-100 text-orange-600',
      variant === 'error' && 'bg-error-100 text-error-600 border-error-600',
      freeWidth || 'max-w-fit',
    ]"
    role="alert"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="flex items-start gap-1 self-center">
      <span
        aria-hidden="true"
        class="inline-flex size-4 shrink-0"
      >
        <component :is="Icon" />
      </span>

      <div
        class="flex flex-wrap gap-1 wrap-break-word"
        :class="{
          'max-w-md': !freeWidth,
          'flex-col': lines === 'multiple',
          'items-center': lines === 'single',
        }"
      >
        <span class="text-base leading-none font-semibold">
          {{ trans(title) }}
        </span>

        <span class="inline-flex flex-wrap items-baseline gap-1.5 text-base leading-none font-normal">
          <span>{{ trans(message) }}</span>
          <bv-btn
            v-if="action?.text"
            class="h-fit cursor-pointer p-0 underline"
            color="currentcolor"
            variant="text"
            @click.stop="onActionClick"
          >
            {{ trans(action?.text) }}
          </bv-btn>
        </span>
      </div>
    </div>

    <bv-btn
      v-if="!hideCloseBtn"
      class="h-fit p-1"
      color="currentcolor"
      variant="text"
      @click="closeToast"
    >
      <bv-alert-close-icon />
    </bv-btn>

    <bv-alert-progress
      v-if="autoClose"
      :duration-ms="durationMs"
      :on-done="closeToast"
      :paused="isHoverPaused"
    />
  </div>
</template>
